import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Organization } from '../../../../domain/organization/dto/Organization';
import { Observable, Subject } from 'rxjs';
import { OrganizationWithAccesses } from '../dto/InitDataDTO';
import { Fund } from '../../../../domain/fund-performance/dto/Fund';
import { ManagementEntity } from '../../../../domain/organization/dto/ManagementEntity';
import { fadeInOutAnimation } from '../../../../util/animations/fade-in-out.animation';
import { FormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { OrganizationsService } from '../../../../domain/organization/components/service/organizations.service';
import { SharedComponentsModule } from '../../../../domain/shared/module/shared-components.module';
import {
  FsGettersSettersService,
} from "../../../../domain/financial-statement/services/common/fs-getters-setters.service";
import { DEFAULT_DROP_DOWN_DELAY_TIME, DelayService } from "../../../service/delay.service";

@Component({
  selector: 'app-org-picker',
  templateUrl: './org-picker.component.html',
  styleUrls: ['./org-picker.component.scss'],
  standalone: true,
  animations: [fadeInOutAnimation(200, 0)],
  imports: [
    SharedComponentsModule,
  ],
  providers: [DelayService],
})

export class OrgPickerComponent implements OnInit, OnChanges, AfterViewInit {
  private _destroyStream: Subject<void> = new Subject<void>();

  @Input() organizationsWithServiceAccess: OrganizationWithAccesses[];
  @Input() selectedOrganization: Organization;
  @Input() currentService: string;
  @Output() entitySelectionEmitter: EventEmitter<SelectedEntityAndOrg> = new EventEmitter();

  showOrgFundPicker = false;

  selectedEntityAndOrg = new SelectedEntityAndOrg();
  selectedOrgOptions: Observable<OrganizationWithAccesses[]>;
  selectedEntity: Fund | ManagementEntity;
  selectedOrgForm: FormGroup<{ selectedOrg: FormControl<string> }> = this._formBuilder.group({
    selectedOrg: new FormControl<string>(''),
  });

  MAX_SELECTED_FUND_NAME_LEN = 30;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private organizationsService: OrganizationsService,
    private fsGetterSetter: FsGettersSettersService,
    private delayService: DelayService,
  ) {}

  resetOrgFundInputValue(): void {
    this.selectedOrgForm.controls.selectedOrg.setValue('');
  }

  ngOnInit(): void {
    // After mouseEnter the input emits empty value, therefore we check previous value from formControl
    this.selectedOrgOptions = this.selectedOrgForm
      .controls.selectedOrg
      .valueChanges.pipe(takeUntil(this._destroyStream),
        startWith(''),
        map((value) => this._filterGroup(value || this.selectedOrgForm.controls.selectedOrg.value)),
      );
    this.organizationsService.selected$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org) => {
        if (!org) {
          return;
        }
        this.selectedOrganization = org;
        // any entity change coming through this subscription will always be type Fund as Management Entities
        // are ony selectable within fs and would be updated in changeSelectedOption in html
        this.selectedEntity = this.selectedOrganization.selectedFund;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedOrganization && this.selectedOrganization) {
      this.selectedEntity = this.selectedOrganization.selectedFund;
    }
    if (this.currentService === 'FS') {
      this.selectedEntity = this.fsGetterSetter.selectedOwnershipEntity.value;
    }
  }

  ngAfterViewInit(): void {
    this.delayService.initSubscribes(DEFAULT_DROP_DOWN_DELAY_TIME, this.showOrgFundDropDown, this.hideOrgFundDropDown);
  }

  activateOrgFundDropDown(): void {
    this.delayService.activate();
  }

  deactivateOrgFundPicker(): void {
    this.delayService.deactivate();
  }

  changeSelectedOption(selectedOption: Fund | ManagementEntity, org: OrganizationWithAccesses): void {
    this.selectedEntity = selectedOption;
    this.selectedEntityAndOrg = { entity: selectedOption, org };
    this.entitySelectionEmitter.emit(this.selectedEntityAndOrg);
  }

  private _filterGroup(searchInput: string): OrganizationWithAccesses[] {
    if (searchInput) {
      return this.filterOrgsBySearchString(this.organizationsWithServiceAccess, searchInput);
    }
    return this.organizationsWithServiceAccess;
  }

  private filterOrgsBySearchString = (opt: OrganizationWithAccesses[], searchInput: string): OrganizationWithAccesses[] => {
    const filterValue = searchInput.toLowerCase();
    return opt.filter(
      (item) =>
        item.organization.name.toLowerCase().includes(filterValue) ||
        item.organization.funds.filter((f) => f.name.toLowerCase().includes(filterValue)).length,
    );
  };

  private showOrgFundDropDown: () => void = () => {
    this.showOrgFundPicker = true;
  };

  private hideOrgFundDropDown: () => void = () => {
    this.showOrgFundPicker = false;
  };
}

export class SelectedEntityAndOrg {
  entity: Fund | ManagementEntity;
  org: OrganizationWithAccesses;
}
